import { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Chip, Typography, alpha } from '@mui/material';
import {
  BannerElementLayout,
  BannerElementStyles,
  ElementPosition,
  StorefrontBanner,
} from '../../ContentManagement/Banners/types/BannerAdmin';
import {
  BannerElementKey,
  BANNER_CORNER_RADIUS,
  BANNER_DESIGN_HEIGHT,
  BANNER_DESIGN_WIDTH,
  DEFAULT_LAYOUT,
  resolveStyle,
} from '../../ContentManagement/Banners/components/BannerPreview';

type Props = {
  banner: StorefrontBanner;
  fixedHeight?: number;
};

const FALLBACK_WIDTH = BANNER_DESIGN_WIDTH;
const FALLBACK_HEIGHT = BANNER_DESIGN_HEIGHT;
function parseJson<T>(value?: string): T | undefined {
  if (!value) return undefined;
  try {
    return JSON.parse(value) as T;
  } catch {
    return undefined;
  }
}

const StorefrontBannerCanvas: FC<Props> = ({ banner, fixedHeight }) => {
  const designWidth = FALLBACK_WIDTH;
  const designHeight = fixedHeight
    || (banner.width > 0 && banner.height > 0
      ? Math.round(FALLBACK_WIDTH * banner.height / banner.width)
      : banner.height || FALLBACK_HEIGHT);
  const color = banner.textColor || '#fff';
  const layout = parseJson<BannerElementLayout>(banner.elementLayoutJson);
  const elementStyles = parseJson<BannerElementStyles>(banner.elementStylesJson);

  const pos = (key: BannerElementKey): ElementPosition => layout?.[key] ?? DEFAULT_LAYOUT[key];
  const style = (key: BannerElementKey) => resolveStyle(key, elementStyles);

  const handleClick = () => {
    if (!banner.ctaUrl) return;
    if (banner.ctaOpensNewTab) {
      window.open(banner.ctaUrl, '_blank', 'noopener');
      return;
    }
    window.location.href = banner.ctaUrl;
  };

  const textSx = (key: BannerElementKey, fallbackColor: string) => {
    const resolvedStyle = style(key);
    return {
      fontSize: `${resolvedStyle.fontSize}px`,
      fontWeight: resolvedStyle.fontWeight,
      ...(resolvedStyle.fontFamily ? { fontFamily: resolvedStyle.fontFamily } : {}),
      ...(resolvedStyle.letterSpacing ? { letterSpacing: `${resolvedStyle.letterSpacing}px` } : {}),
      textTransform: resolvedStyle.textTransform,
      fontStyle: resolvedStyle.fontStyle,
      color: resolvedStyle.color || fallbackColor,
      lineHeight: 1.2,
      ...(resolvedStyle.padding ? { padding: `${resolvedStyle.padding}px` } : {}),
      ...(resolvedStyle.margin ? { margin: `${resolvedStyle.margin}px` } : {}),
      ...(resolvedStyle.height ? { height: `${resolvedStyle.height}px` } : {}),
      ...(resolvedStyle.width ? { width: `${resolvedStyle.width}px` } : {}),
      overflowWrap: 'anywhere',
    };
  };

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(0);

  const measure = useCallback(() => {
    if (!wrapperRef.current) return;
    setScale(Math.min(1, wrapperRef.current.clientWidth / designWidth));
  }, [designWidth]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    const ro = new ResizeObserver(measure);
    if (wrapperRef.current) ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, [measure]);

  return (
    <Box
      ref={wrapperRef}
      onClick={banner.ctaUrl ? handleClick : undefined}
      sx={{
        width: '100%',
        height: `${designHeight * scale}px`,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: `${BANNER_CORNER_RADIUS}px`,
        cursor: banner.ctaUrl ? 'pointer' : 'default',
        backgroundColor: banner.backgroundColor || '#1A1A2E',
        backgroundImage: banner.imageUrl ? `url(${banner.imageUrl})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        color,
      }}
    >
      {banner.imageUrl && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.10) 100%)',
            pointerEvents: 'none',
          }}
        />
      )}
      <Box
        sx={{
          width: `${designWidth}px`,
          height: `${designHeight}px`,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          position: 'absolute',
          top: 0,
          left: 0,
          color,
        }}
      >
        {banner.tagText && (
          <Box sx={{ position: 'absolute', left: `${pos('tag').x}%`, top: `${pos('tag').y}%`, zIndex: 1, maxWidth: `${style('tag').maxWidth}%`, ...(style('tag').margin ? { margin: `${style('tag').margin}px` } : {}) }}>
            <Chip
              label={banner.tagText}
              size="small"
              sx={{
                background: `linear-gradient(90deg, ${style('tag').backgroundColor || banner.tagColor || '#16A34A'} 0%, ${alpha(style('tag').backgroundColor || banner.tagColor || '#16A34A', 0)} 100%)`,
                color: style('tag').color || '#fff',
                fontWeight: style('tag').fontWeight,
                fontSize: `${style('tag').fontSize}px`,
                ...(style('tag').fontFamily ? { fontFamily: style('tag').fontFamily } : {}),
                letterSpacing: style('tag').letterSpacing ? `${style('tag').letterSpacing}px` : '0.04em',
                textTransform: style('tag').textTransform,
                height: style('tag').height || Math.max(22, style('tag').fontSize + 12),
                ...(style('tag').width ? { width: `${style('tag').width}px` } : {}),
                ...(style('tag').padding ? { padding: `${style('tag').padding}px` } : {}),
                borderRadius: '6px',
              }}
            />
          </Box>
        )}

        {banner.title && (
          <Box sx={{ position: 'absolute', left: `${pos('title').x}%`, top: `${pos('title').y}%`, zIndex: 1, maxWidth: `${style('title').maxWidth}%` }}>
            <Typography component="div"
              sx={textSx('title', color)}>
              {banner.title}
            </Typography>
          </Box>
        )}

        {banner.subtitle && (
          <Box sx={{ position: 'absolute', left: `${pos('subtitle').x}%`, top: `${pos('subtitle').y}%`, zIndex: 1, maxWidth: `${style('subtitle').maxWidth}%` }}>
            <Typography component="div"
              sx={textSx('subtitle', alpha(color === '#fff' || color === '#FFFFFF' ? '#fff' : color, 0.85))}>
              {banner.subtitle}
            </Typography>
          </Box>
        )}

        {banner.ctaText && (
          <Box sx={{ position: 'absolute', left: `${pos('cta').x}%`, top: `${pos('cta').y}%`, zIndex: 1, maxWidth: `${style('cta').maxWidth}%`, ...(style('cta').margin ? { margin: `${style('cta').margin}px` } : {}) }}>
            <Chip
              label={banner.ctaText}
              sx={{
                backgroundColor: style('cta').backgroundColor || '#fff',
                color: style('cta').color || '#1A1A2E',
                fontWeight: style('cta').fontWeight,
                fontSize: `${style('cta').fontSize}px`,
                ...(style('cta').fontFamily ? { fontFamily: style('cta').fontFamily } : {}),
                ...(style('cta').letterSpacing ? { letterSpacing: `${style('cta').letterSpacing}px` } : { letterSpacing: '0.02em' }),
                textTransform: style('cta').textTransform,
                fontStyle: style('cta').fontStyle,
                height: style('cta').height || Math.max(32, style('cta').fontSize + 18),
                ...(style('cta').width ? { width: `${style('cta').width}px` } : {}),
                ...(style('cta').padding ? { padding: `${style('cta').padding}px` } : {}),
                borderRadius: '5px',
                px: 1.5,
                '& .MuiChip-label': {
                  px: 1.5,
                },
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default StorefrontBannerCanvas;
